/**
 * Product Form Components
 *
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    // Prefer cart-form inside cart drawer when present (product/collection pages); fallback to template-cart (cart page)
    const cartDrawer = document.getElementById('component-cart-drawer');
    this.cartElement = cartDrawer?.querySelector('cart-form') || Utility.DomSelector.$('cart-form');
    this.cartSectionId = this.cartElement?.dataset?.sectionId || 'template-cart';

    this.boundOnSubmitHandler = this.onSubmitHandler.bind(this);
  }

  /**
   * Resolve form nodes on connect — required for AJAX-injected quickshop markup
   * where children are not available in the constructor.
   */
  refreshFormRefs() {
    this.form = Utility.DomSelector.$('form', this);
    this.submitButton = Utility.DomSelector.$('[type="submit"]', this);
    this.qtyInput = Utility.DomSelector.$('[data-qty-input]', this);
    this.productId = this.dataset.productid;
  }

  connectedCallback() {
    this.refreshFormRefs();
    this.form?.addEventListener('submit', this.boundOnSubmitHandler);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.boundOnSubmitHandler);
  }

  updateProductDetails(container, variant, html) {
    if (!container || !variant) {
      this.toggleAddButton('disable');
      return;
    }

    this.price = Utility.DomSelector.$('[data-current-price]', container);
    if(this.price.closest('.product-block-pair-with')) return;
    this.comparePrice = Utility.DomSelector.$('[data-compare-price]', container);
    this.discountPercentage = Utility.DomSelector.$('[data-discount-percentage]', container);
    this.featuredImage = Utility.DomSelector.$('[data-featured-image]', container);
    this.productSlider = Utility.DomSelector.$('[data-product-slider]', container);

    this.toggleAddButton('enable', variant.available, variant.title);
    this.updateURLandID(variant);
    this.updateDynamicSections(container, html);
    this.priceUpdate(variant);
    this.updateMedia(variant.featured_media);
  }

   /**
   * Render Price details based on current variant
   */
   priceUpdate(variant) {
    if (!this.price) return;

    this.price.textContent = Utility.formatMoney(variant.price, globalThis.themeData.money_format, { hideTrailingZeros: true });
    this.price.classList.toggle('discounted-price', variant.compare_at_price > variant.price);

    if(this.comparePrice) {
      this.comparePrice.innerHTML = `<del class="money">${Utility.formatMoney(variant.compare_at_price, globalThis.themeData.money_format, { hideTrailingZeros: true })}</del>`;
      this.comparePrice.classList.toggle('d-none', !variant.compare_at_price || variant.compare_at_price <= 0 || variant.compare_at_price === variant.price);
    }
    
    if(this.discountPercentage){
      const discountPercentage = variant.compare_at_price - variant.price;
      const discountPercentageValue = Math.round((discountPercentage / variant.compare_at_price) * 100);
      this.discountPercentage.textContent = `(-${discountPercentageValue}%)`;
      this.discountPercentage.classList.toggle('d-none', variant.compare_at_price <= variant.price);
    }
  }

  /**
   * Updates dynamic variant-dependent sections (SKU, inventory, quantity, volume pricing)
   * Scoped to the given container so it works for both PDP and product cards
   * The HTML for the product card comes from sections/product-card-rendering.liquid while the Product page uses sections/template-product.liquid.
   * @param {Element} container
   * @param {Document} html - Parsed section HTML from fetchProductHTML
   */
  updateDynamicSections(container, html) {
    if (!container || !html) return;

    Utility.DOM.updateSelectors([
      `[data-variant-inventory]`,
      `[data-variant-sku]`,
      `[data-product-quantity-picker]`,
      `[data-variant-volumepricing]`
      /*,`[data-compare-price]`,
      `[data-current-price]`,
      `[data-discount-percentage]`*/
    ], html, container);
  }

  /**
   * Toggle the button based on product availability ( add to cart / soldOut )
   * @param {Text} status enable / disable
   * @param {Boolean} variantAvailable true/false
   */
  toggleAddButton(status, variantAvailable, variantTitle) {
    if (!this.submitButton) return;
    const isUpsell = this.form?.classList.contains('cart-upsell-form');
    const addText = Utility.DomSelector.$('.add-text', this.submitButton);
    
    if (status === 'disable' || (!variantAvailable)) {
        this.submitButton.setAttribute('disabled', true);
        if(addText) {
          const text = status === 'disable' ? globalThis.variantStrings.unavailable : globalThis.variantStrings.soldOut;
          addText.textContent = text;
          this.submitButton.setAttribute('aria-label', `${text}: ${variantTitle}`);
        }
    } else {
        this.submitButton.removeAttribute('disabled');
        if(addText) {
          const text = isUpsell ? globalThis.variantStrings.upsellAddText : globalThis.variantStrings.addToCart;
          addText.textContent = text;
          this.submitButton.setAttribute('aria-label', `${text}: ${variantTitle}`);
        }
    }
  }

  /**
   * Update URL on variant change event
   * @param {JSON} currentVariant 
   */
  updateURLandID(currentVariant) {
    if (!currentVariant || !this.form) return;

    if (this.dataset.format === 'product-page') {
      const rawPath = this.form.dataset.url || window.location.pathname;
      const path = String(rawPath).split('?')[0];
      Utility.URL.replaceUrl(`variant=${currentVariant.id}`, { path });
    }
    Utility.DomSelector.$('input[name="id"]', this.form).value = currentVariant.id;
  }

  /**
   * Render selected variant image/slide
   * @param {object} variantMedia
   */
  updateMedia(variantMedia) {
    if (!variantMedia) return;

    if(this.featuredImage && variantMedia.preview_image?.src){
      this.featuredImage.src = `${variantMedia.preview_image.src}&width=850`;
      this.featuredImage.removeAttribute('srcset');
      Utility.CSS.remove(this.featuredImage, 'lozad');
    }else if(this.productSlider){
      const imageSlide = Utility.DomSelector.$(`[data-mediaID="${variantMedia.id}"]`, this.productSlider);
      if(imageSlide){
        const slideIndex = Array.from(imageSlide.parentNode.children).indexOf(imageSlide);
        if (this.productSlider?.swiper) this.productSlider.swiper.slideTo(slideIndex, 0, false);
      }
    }
  }

  /**
   * Product Form Submit event
   *
   * @param {evt} Event instance
   */
  async onSubmitHandler(evt) {
    evt.preventDefault();

    this.refreshFormRefs();
    if (!this.form) return;

    Utility.Animate.setLoading(this.submitButton, true);

    const formData = this.prepareFormData();

    try {
      const res = await Utility.Fetch.json(`${routes.cart_add_url}.js`, 
        Utility.Fetch.config('javascript', { body: formData })
      );

      await this.handleSubmitResponse(res);
    } catch (error) {
      const message = Utility.extractErrorMessage(error) || 'Failed to add to cart';
      Utility.handleError(message, 'Add to Cart');
      Utility.announceToScreenReader(message, 'assertive');
    } finally {
      Utility.Animate.setLoading(this.submitButton, false);
    }
  }

  /**
   * Live cart section id — prefer the drawer so AJAX-injected quickshop forms
   * always request component-cart-drawer, not a stale constructor fallback.
   */
  resolveCartSectionId() {
    const cartDrawer = document.getElementById('component-cart-drawer');
    const cartForm = cartDrawer?.querySelector('cart-form') || this.cartElement || Utility.DomSelector.$('cart-form');
    return cartForm?.dataset?.sectionId || this.cartSectionId || 'template-cart';
  }

  /**
   * Prepares form data for cart API submission
   * @returns {FormData} Form data with sections parameter
   */
  prepareFormData() {
    const formData = new FormData(this.form);
    const sectionId = this.resolveCartSectionId();
    const cartDrawer = document.getElementById('component-cart-drawer');
    formData.append('sections', sectionId);

    // Give Shopify an explicit render URL so drawer HTML returns off-collection too
    if (cartDrawer && !window.location.pathname.includes('/cart')) {
      formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
    } else if (sectionId === 'template-cart' && !window.location.pathname.includes('/cart')) {
      formData.append('sections_url', globalThis.routes?.cart_fetch_url || '/cart');
    }
    return formData;
  }

   /**
   * Handles API response from cart submission
   * @param {Object|String} response - API response (JSON object or string)
   */
  async handleSubmitResponse(response) {
    let res = ((typeof response == 'object') ? response : JSON.parse(response));
    const sectionId = this.resolveCartSectionId();
    const cartHTML = res.sections?.[sectionId] || res.sections?.[this.cartSectionId];
    const parsedCartHTML = cartHTML ? Utility.DOM.parse(cartHTML) : null;
    const fromQuickshop =
      this.dataset.format === 'quickview' || Boolean(this.closest('#quickshop-drawer'));
    const quickshopDrawer = document.getElementById('quickshop-drawer');
    const openedFromCart = fromQuickshop && quickshopDrawer?.dataset?.openedFromCart === 'true';
    const cartDrawer = document.getElementById('component-cart-drawer');
    const cartTrigger = Utility.DomSelector.$('[data-toggledrawer="component-cart-drawer"]');

    if (fromQuickshop) {
      ProductForm.closeQuickshopDrawer();
    }

    await Utility.Cart.sync(parsedCartHTML);

    eventBus.publish(THEME_EVENTS.cartUpdate, {
      data: {
        action: 'add-item',
        item: res,
        section: parsedCartHTML,
        // Prefer cart trigger after quickshop close — submit button is removed with drawer content
        elementToFocus: fromQuickshop ? (cartTrigger || document.body) : this.submitButton
      }
    });

    if (openedFromCart) {
      ProductForm.reopenCartDrawer(cartDrawer, cartTrigger);
    }

    if (this.qtyInput) this.qtyInput.value = this.qtyInput.min || 1;
    if (!this.cartElement) {
      Utility.handleSuccess(res.title, 'Added to Cart');
    }
    const itemCount = globalThis.themeData?.cart?.item_count ?? 0;
    Utility.announceToScreenReader(`${res.title} added to cart. Cart now has ${itemCount} item${itemCount !== 1 ? 's' : ''}`, 'assertive');
  }

  /**
   * Reopen cart after quickshop + overlay have finished closing
   * so hideOverlay cannot immediately shut the cart.
   */
  static reopenCartDrawer(cartDrawer, cartTrigger) {
    if (!cartDrawer || typeof cartDrawer.openDrawer !== 'function') return;

    const openCart = () => {
      cartDrawer.openDrawer(cartTrigger || document.body);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(openCart, 50);
      });
    });
  }

  static closeQuickshopDrawer() {
    if (typeof window.closeQuickshopDrawer === 'function') {
      window.closeQuickshopDrawer();
      return;
    }

    const drawer = Utility.DomSelector.byId('quickshop-drawer');
    if (!drawer) return;

    drawer.removeAttribute('data-opened-from-cart');
    if (!drawer.classList.contains('open') && !drawer.isOpen) return;

    // Force close even if isOpen flag is out of sync with the visible state
    drawer.isOpen = true;
    if (typeof drawer.closeDrawer === 'function') {
      drawer.closeDrawer();
      return;
    }

    Utility.CSS.remove(drawer, 'open');
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
    const body = Utility.DomSelector.$('[data-quickshop-body]', drawer);
    if (body) body.innerHTML = '';
  }
}

customElements.define('product-form', ProductForm);
